import {
  gql,
  MutationFunction as MutationFn,
  FetchResult,
} from '@apollo/client';
import { DocumentNode } from 'graphql';
import { fieldDefsToFields } from '../../../../../Utils/columnDefsToFields';
import { ICollection } from './ObjectUpdateMutation';
import { fromPairs, pair, mergeAll } from 'ramda';

export const createUpdateMutation = (
  collectionName: string,
  attributePaths: string[]
): DocumentNode => {
  return gql`mutation UpdateFieldMutation($databaseName: String!, $id: ID!, $_version: Int!, $values: TEDInput${collectionName}NewValues!) {
      db(name: $databaseName) {
        ${collectionName}_Update(id: $id, _version: $_version, values: $values) {
          ${fieldDefsToFields(collectionName)(attributePaths)}
        }
      }
    }`;
};

export const createUpdateGroupMutation = (
  collections: ICollection[]
): DocumentNode => {
  const collectionParamsString = collections
    .map(
      collection =>
        `$${collection.name}Id: ID!, $${collection.name}Version: Int!, $${collection.name}Values: TEDInput${collection.name}NewValues!`
    )
    .join(', ');

  const collectionUpdateMutationsString = collections
    .map(
      collection => `${collection.name}_Update(id: $${
        collection.name
      }Id, _version: $${collection.name}Version, values: $${
        collection.name
      }Values) {
    ${fieldDefsToFields(collection.name)(
      collection.attributes.map(attr => attr.attribute.AttributeName)
    )}
  }`
    )
    .join('\n');

  return gql`mutation UpdateFieldGroupMutation($databaseName: String!, ${collectionParamsString}) {
      db(name: $databaseName) {
        ${collectionUpdateMutationsString}
      }
    }`;
};

const getValuesForCollection = (
  collection: ICollection,
  values: IValues
): IValues =>
  fromPairs(
    collection.attributes.map(attr =>
      pair(attr.attribute.AttributeName, values[attr.attributePath])
    )
  );

export const getUpdateGroupMutationCollectionVariables = (
  collections: ICollection[],
  values: IValues
): { [key: string]: any } => {
  const collectionVariables = collections.map(collection => {
    if (collection._version == null || collection.id == null) {
      throw new Error(
        'Tried to mutate collection with undefined id or version'
      );
    }

    return {
      [`${collection.name}Id`]: collection.id,
      [`${collection.name}Version`]: collection._version,
      [`${collection.name}Values`]: getValuesForCollection(collection, values),
    };
  });

  return mergeAll(collectionVariables);
};

export const getUpdateGroupMutationCollectionOptimisticResponse = (
  collections: ICollection[],
  values: IValues
) =>
  mergeAll(
    collections.map(collection => {
      return {
        [`${collection.name}_Update`]: {
          id: collection.id || '',
          _version: collection._version || 0 + 1,
          ...getValuesForCollection(collection, values),
          __typename: `TED${collection.name}`,
        },
      };
    })
  );

export interface IValues {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Ex:
 * {
 *  LRLeverantorId	"KEJDMyMmcRAdKNSdvPbPYrtcoPdjSbUF"
 *  LRLeverantorValues	{"KUNDNR":"22345","ANGE_KUNDNR_SOM_FAKTREF":true,"ORDINARIE_REFERENS_TYP":"BESTALLARREFERENS_1","RIKSBANKSKOD":"lol","RABATT":4,"REGDATUM":"2012-01-04"}
 *  LRLeverantorVersion	42
 *  LRLeverantorkatId	"ivcaPDPYbmpIiejIGfLfVpJnCDsvzLik"
 *  LRLeverantorkatValues	{"BENAMNING":"cr23"}
 *  LRLeverantorkatVersion	1
 *  databaseName	"Petter"
 * }
 */
export interface IVariables {
  databaseName: string;
  [key: string]: any;
}

/**
 * Ex:
 * {
 *    "LRLeverantor_Update": {
 *      "id": "KEJDMyMmcRAdKNSdvPbPYrtcoPdjSbUF",
 *      "_version": 43,
 *      "KUNDNR": "22345",
 *      "ANGE_KUNDNR_SOM_FAKTREF": true,
 *      "ORDINARIE_REFERENS_TYP": "BESTALLARREFERENS_1",
 *      "RIKSBANKSKOD": "lol",
 *      "RABATT": 4,
 *      "REGDATUM": "2012-01-04",
 *      "__typename": "TEDLRLeverantor"
 *    },
 *    "LRLeverantorkat_Update": {
 *      "id": "ivcaPDPYbmpIiejIGfLfVpJnCDsvzLik",
 *      "_version": 1,
 *      "BENAMNING": "cr23",
 *      "__typename": "TEDLRLeverantorkat"
 *    },
 *    "__typename": "TEDAvenyDBMutation"
 *  }
 */
export interface IData {
  [key: string]: any;
}

export type ObjectUpdateMutationFnType = MutationFn<IData, IVariables>;
export type ObjectUpdateMutationReturnType = Promise<void | FetchResult<IData>>;
